import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye } from "lucide-react";
import { useT } from "@/lib/i18n";
import { api, ApiError } from "@/lib/api";

// Top-nav pulse badge — unique sessions on the storefront in the last
// 5 minutes. Mirrors miluliem's LiveVisitorsBadge: polls every 30s,
// shows a green pulse dot when count > 0, gray when zero, click jumps
// to /performance/visitors for the full breakdown.

const POLL_INTERVAL_MS = 30_000;

export function LiveVisitorsBadge() {
  const navigate = useNavigate();
  const t = useT();
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const fetchCount = async () => {
      try {
        const json = await api<{ data: { live_visitors: number } }>(
          "/api/v1/admin/live-visitors",
        );
        if (!cancelled) setCount(json.data?.live_visitors ?? 0);
      } catch (err) {
        // 401s during initial mount before auth resolves are normal —
        // swallow rather than spamming the console.
        if (!cancelled && !(err instanceof ApiError && err.status === 401)) {
          // eslint-disable-next-line no-console
          console.error("[LiveVisitorsBadge] fetch failed", err);
        }
      }
    };

    void fetchCount();
    timer = setInterval(fetchCount, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, []);

  const live = (count ?? 0) > 0;
  return (
    <button
      onClick={() => navigate("/performance/visitors")}
      className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      title={t("topnav.liveVisitorsTooltip", "Live visitors on the storefront (last 5 min)")}
    >
      <span
        className={`inline-block h-1.5 w-1.5 rounded-full ${
          live ? "animate-pulse bg-emerald-500" : "bg-gray-300"
        }`}
      />
      <Eye size={14} />
      <span className="tabular-nums">{count ?? "—"}</span>
    </button>
  );
}
